import { useEffect, useState } from 'react';
import { ExternalLink, Gamepad2, ScrollText } from 'lucide-react';
import AdminShell from '../../components/AdminShell.jsx';
import { useFlash } from '../../context/ToastContext.jsx';
import Loader from '../../components/Loader.jsx';
import { api } from '../../api/client.js';

// /admin/registration — the rules text on the public player-registration
// page (kec-register.pages.dev). Saving writes registration_settings
// (migration 0040); the page fetches it on every load, so a change is live
// within a minute with no deploy. One registration link today, so this is
// one form rather than a list of links.

const PAGE_URL = 'https://kec-register.pages.dev/';
const GAMES = [
  { key: 'rivals', name: 'Marvel Rivals' },
  { key: 'valorant', name: 'Valorant' },
];
// Every section has a "show on the page" switch; off keeps the text saved.
const SECTIONS = ['general', ...GAMES.map((g) => g.key)];
const FORMAT_HINT = 'One rule per line. Start a line with "-", "1." or "1-" to make a list; leave a blank line between sections. '
  + 'Write both languages in this box, in any order: English lines show in the left column of the card, Arabic lines in the right.';

function RulesField({ id, label, hint, value, onChange, shown, onToggle, disabled }) {
  return (
    <>
      <div className="form-check form-switch mb-2">
        <input
          className="form-check-input"
          type="checkbox"
          role="switch"
          id={`${id}-show`}
          checked={shown}
          disabled={disabled}
          onChange={(e) => onToggle(e.target.checked)}
        />
        <label className="form-check-label" htmlFor={`${id}-show`}>
          Show on the page{shown ? '' : ' — hidden for now, the text stays saved'}
        </label>
      </div>
      <label className="adm-flabel" htmlFor={id}>{label}</label>
      <textarea
        id={id}
        className="adm-utextarea"
        dir="auto"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder="Nothing yet — this section stays hidden on the page until it has text."
        spellCheck={false}
      />
      <small className="text-muted d-block mt-2">{hint}</small>
    </>
  );
}

export default function RegistrationSettings() {
  const [saved, setSaved] = useState(null);
  const [general, setGeneral] = useState('');
  const [games, setGames] = useState({});
  const [visible, setVisible] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const setFlash = useFlash();

  const applySaved = (settings) => {
    setSaved(settings);
    setGeneral(settings.generalRules ?? '');
    setGames({ ...settings.gameRules });
    setVisible({ ...settings.visibility });
  };

  useEffect(() => {
    let alive = true;
    api.adminGetRegistrationSettings()
      .then((res) => { if (alive) applySaved(res.settings); })
      .catch((e) => { if (alive) setFlash({ kind: 'danger', msg: e.message }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const dirty = !!saved && (
    general !== (saved.generalRules ?? '')
    || GAMES.some((g) => (games[g.key] ?? '') !== (saved.gameRules?.[g.key] ?? ''))
    || SECTIONS.some((s) => (visible[s] ?? true) !== (saved.visibility?.[s] ?? true))
  );

  async function save() {
    setBusy(true);
    setFlash(null);
    try {
      const res = await api.adminPutRegistrationSettings({ generalRules: general, gameRules: games, visibility: visible });
      applySaved(res.settings);
      setFlash({ kind: 'success', msg: 'Saved. The page picks it up within a minute.' });
    } catch (e) {
      setFlash({ kind: 'danger', msg: e.message });
    } finally {
      setBusy(false);
    }
  }

  const savedAt = saved?.updatedAt ? new Date(saved.updatedAt).toLocaleString() : null;

  return (
    <AdminShell
      crumb="Registration"
      actions={(
        <a className="adm-act adm-act--quiet" href={PAGE_URL} target="_blank" rel="noreferrer">
          <ExternalLink size={14} aria-hidden="true" /> View page
        </a>
      )}
    >
      <header className="adm-head adm-rv" style={{ animationDelay: '80ms' }}>
        <h1 className="adm-head__title">Registration</h1>
        <p className="adm-head__sub">
          Rules shown on the player-registration page. Changes go live on the next page load, no deploy needed.
        </p>
      </header>


      {loading && <Loader label="Loading…" />}

      {saved && (
        <div className="adm-form-col">
          <section className="adm-esec adm-rvl" id="sec-general" style={{ animationDelay: '140ms' }}>
            <div className="adm-group__head">
              <span className="adm-initial adm-initial--icon" aria-hidden="true"><ScrollText size={17} /></span>
              <span className="adm-group__name">General rules</span>
              <span className="adm-group__summary">above REGISTER · shown again after registering</span>
              {visible.general === false && <span className="adm-chip adm-chip--amber">Hidden</span>}
            </div>
            <div className="adm-esec__body">
              <RulesField
                id="rs-general"
                label="Tournament rules"
                hint={FORMAT_HINT}
                value={general}
                onChange={setGeneral}
                shown={visible.general !== false}
                onToggle={(on) => setVisible((prev) => ({ ...prev, general: on }))}
                disabled={busy}
              />
            </div>
          </section>

          {GAMES.map((g, i) => (
            <section
              key={g.key}
              className="adm-esec adm-rvl"
              id={`sec-${g.key}`}
              style={{ animationDelay: `${200 + i * 60}ms` }}
            >
              <div className="adm-group__head">
                <span className="adm-initial adm-initial--icon adm-initial--pink" aria-hidden="true"><Gamepad2 size={17} /></span>
                <span className="adm-group__name">{g.name} rules</span>
                <span className="adm-group__summary">shown once a player picks {g.name}, before her details</span>
                {visible[g.key] === false && <span className="adm-chip adm-chip--amber">Hidden</span>}
              </div>
              <div className="adm-esec__body">
                <RulesField
                  id={`rs-${g.key}`}
                  label={`${g.name} rules`}
                  hint={FORMAT_HINT}
                  value={games[g.key] ?? ''}
                  onChange={(v) => setGames((prev) => ({ ...prev, [g.key]: v }))}
                  shown={visible[g.key] !== false}
                  onToggle={(on) => setVisible((prev) => ({ ...prev, [g.key]: on }))}
                  disabled={busy}
                />
              </div>
            </section>
          ))}

          <div className="d-flex align-items-center gap-3 flex-wrap mt-3">
            <button
              type="button"
              className="btn-pill btn-pill--primary"
              disabled={busy || !dirty}
              onClick={save}
            >
              {busy ? 'Saving…' : 'Save rules'}
            </button>
            <span className="text-muted small">
              {dirty
                ? 'Unsaved changes'
                : savedAt
                  ? `Last saved ${savedAt}${saved.updatedBy ? ` by ${saved.updatedBy}` : ''}`
                  : 'Never saved'}
            </span>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
